import { api } from "./api";
import { getAccessToken } from "./auth";

/** In-flight dedupe: Auth + PushHealthBanner can call status in the same tick (Strict Mode doubles effects). */
let _subscriptionStatusInflight = null;

/** Call after POST /subscribe so the next status read is not shared with a stale in-flight request. */
export function invalidatePushSubscriptionStatus() {
  _subscriptionStatusInflight = null;
}

export async function getPushSubscriptionStatus() {
  if (typeof window === "undefined") return { subscribed: false, subscription_count: 0 };
  if (_subscriptionStatusInflight) return _subscriptionStatusInflight;

  _subscriptionStatusInflight = (async () => {
    try {
      const token = getAccessToken();
      if (!token) return { subscribed: false, subscription_count: 0 };
      const statusRes = await api.get("/notifications/subscription-status", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = statusRes?.data?.data ?? statusRes?.data ?? {};
      return {
        subscribed: !!data.subscribed,
        subscription_count: Number(data.subscription_count || 0),
      };
    } catch {
      return { subscribed: false, subscription_count: 0 };
    } finally {
      _subscriptionStatusInflight = null;
    }
  })();

  return _subscriptionStatusInflight;
}

function isStandalonePwa() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true ||
    window.__IS_PWA === true
  );
}

function isHttpsOrLocalhost() {
  if (typeof window === "undefined") return false;
  const { protocol, hostname } = window.location;
  if (protocol === "https:") return true;
  return hostname === "localhost" || hostname === "127.0.0.1";
}

/** Why Web Push cannot run in this tab (null = OK to attempt registration). */
export function getWebPushBlockReason() {
  if (typeof window === "undefined") return "ssr";
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return "unsupported";
  if (!isHttpsOrLocalhost()) return "not_https";
  return null;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

let _lifecycleListenersInstalled = false;

/** Listen for SW telling us the push subscription changed (browser/PWA must re-sync with backend). */
export function installPushSubscriptionLifecycleListeners() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator) || _lifecycleListenersInstalled) return;
  _lifecycleListenersInstalled = true;
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event?.data?.type !== "PUSH_SUBSCRIPTION_CHANGED") return;
    if (!getAccessToken()) return;
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      registerPushNotifications({ allowPermissionPrompt: false }).catch(() => {});
    }
  });
}

/**
 * Register Web Push with the backend.
 * @param {{ allowPermissionPrompt?: boolean }} [options]
 *   When `allowPermissionPrompt` is true (e.g. login submit or "Enable" button), the browser
 *   may show the notification permission dialog. Chrome often denies `requestPermission()` when
 *   it is not triggered from a user gesture, so background calls (e.g. after `useEffect`) should
 *   pass false and only sync when permission is already `granted`.
 */
export async function registerPushNotifications(options = {}) {
  const { allowPermissionPrompt = false } = options;

  if (typeof window === "undefined" || !("serviceWorker" in navigator) || !("PushManager" in window)) {
    return { ok: false, reason: "unsupported" };
  }

  try {
    const token = getAccessToken();
    if (!token) return { ok: false, reason: "no_token" };

    const standalone = isStandalonePwa();
    console.log(`[push] start mode=${standalone ? "pwa" : "browser"} origin=${window.location.origin}`);

    if (!isHttpsOrLocalhost()) {
      console.warn("[push] blocked: Push requires HTTPS (or localhost). Current:", window.location.href);
      return { ok: false, reason: "not_https" };
    }

    const registration = await navigator.serviceWorker.ready;

    // Check if already subscribed (browser tab and installed PWA have separate subs)
    let subscription = await registration.pushManager.getSubscription();
    console.log("[push] subscription exists:", subscription ? "YES" : "NO");

    const currentPermission = Notification.permission;
    let permission = currentPermission;
    if (permission === "default") {
      if (!allowPermissionPrompt) {
        console.log("[push] permission default — skipping prompt (use a button/login tap with allowPermissionPrompt: true)");
        return { ok: false, reason: "needs_user_gesture" };
      }
      permission = await Notification.requestPermission();
    }
    console.log("[push] permission:", permission);
    if (permission !== "granted") return { ok: false, reason: permission };

    // If permission is granted but backend has no subscription (e.g. expired / deleted),
    // force a re-subscribe by clearing any old local subscription so we can create a fresh one.
    try {
      let { subscribed } = await getPushSubscriptionStatus();
      if (!subscribed && subscription) {
        try {
          await subscription.unsubscribe();
        } catch {}
        subscription = null;
        subscription = await registration.pushManager.getSubscription();
        ({ subscribed } = await getPushSubscriptionStatus());
      }
    } catch {
      // ignore status failures; best-effort and continue with subscribe flow
    }

    if (!subscription) {
      const keyRes = await api.get("/notifications/vapid-public-key", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const vapidPublicKey = keyRes?.data?.data?.public_key ?? keyRes?.data?.public_key ?? null;

      if (!vapidPublicKey) {
        console.warn("[push] VAPID public key missing on backend; cannot subscribe.");
        return { ok: false, reason: "no_vapid_public_key" };
      }

      console.log("[push] creating new subscription…");
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
        });
        console.log("[push] subscription created.");
      } catch (err) {
        console.warn("[push] subscribe failed:", err);
        return { ok: false, reason: "subscribe_failed" };
      }
    }

    // Always POST current subscription when permission is granted so backend stays in sync
    // (PWA vs browser, subscription rotation, multi-device — idempotent upsert by endpoint).
    if (subscription) {
      const subJSON = subscription.toJSON();
      const device = isStandalonePwa() ? "pwa" : "browser";
      await api.post(
        "/notifications/subscribe",
        {
          endpoint: subJSON.endpoint,
          keys: {
            p256dh: subJSON.keys.p256dh,
            auth: subJSON.keys.auth,
          },
          device,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      invalidatePushSubscriptionStatus();
      console.log(`[push] subscription synced to backend (device=${device}).`);
    }
    return { ok: true, reason: "subscribed" };
  } catch (err) {
    console.error("Service worker registration failed:", err);
    return { ok: false, reason: "error" };
  }
}

export async function checkMentions() {
  try {
    const res = await api.get("/notifications/unread");
    return res.data?.data?.items || [];
  } catch (e) {
    return [];
  }
}
